import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { useTheme } from '../lib/useTheme';
import { supabase } from '../lib/supabaseClient';
import UserAvatar from '../lib/UserAvatar';
import { compressImage } from '../lib/compressImage';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

const TIER_LABELS = { free_observer:'Free Observer', doer:'Doer', spark_seeker:'Doer', momentum_builder:'Momentum Builder', growth_mastermind:'Growth Mastermind', onsite_mexico:'Onsite Growth MX', admin:'Admin' };

export default function Perfil() {
  const navigate = useNavigate();
  const { user, profile, membership, signOut, refreshProfile } = useAuth();
  const { isMobile } = useScreen();
  const t = useTheme();

  const [form, setForm] = useState({ full_name:'', business_name:'', business_type:'', business_stage:'', business_description:'', city:'', instagram:'', tiktok:'', facebook:'', presentacion_descripcion:'' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        business_name: profile.business_name || '',
        business_type: profile.business_type || '',
        business_stage: profile.business_stage || '',
        business_description: profile.business_description || '',
        city: profile.city || '',
        instagram: profile.instagram || '',
        tiktok: profile.tiktok || '',
        facebook: profile.facebook || '',
        presentacion_descripcion: profile.presentacion_descripcion || '',
      });
    }
  }, [profile]);

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const saveProfile = async () => {
    if (!user || saving) return;
    setSaving(true);
    const { error } = await supabase.from('profiles').update({
      ...form, updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    if (error) console.error('Save error:', error.message);
    else { setSaved(true); setTimeout(() => setSaved(false), 3000); await refreshProfile(); }
    setSaving(false);
  };

  const uploadFile = async (file, bucket, field) => {
    if (!user || !file) return;
    setUploading(true);
    file = await compressImage(file);
    const ext = file.name.split('.').pop();
    const path = `${user.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
    if (upErr) { console.error('Upload error:', upErr.message); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
    await supabase.from('profiles').update({ [field]: publicUrl }).eq('id', user.id);
    await refreshProfile();
    setUploading(false);
  };

  const nombre = profile?.full_name?.split(' ')[0] || 'ahí';
  const inputStyle = { width:'100%', padding:'10px 14px', border:'1px solid rgba(255,255,255,0.1)', borderRadius:12, fontSize:14, fontFamily:font, background:'rgba(255,255,255,0.06)', color:'#fff', outline:'none', boxSizing:'border-box' };
  const labelStyle = { fontSize:12, color:t.textSecondary, display:'block', marginBottom:4, fontWeight:500 };

  return (
    <div style={{ background:t.bg, fontFamily:font, color:t.text, minHeight:'100vh', overflowWrap:'break-word' }}>
      {/* NAV */}
      <nav style={{ position:'sticky', top:0, zIndex:100, background:t.navBg, backdropFilter:'blur(12px)', borderBottom:`0.5px solid ${t.border}` }}>
        <div style={{ maxWidth:960, margin:'0 auto', padding:'0 1.5rem', height:60, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <button onClick={()=>navigate('/sala')} style={{ background:'none', border:'none', color:C.turquesa, fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:font }}>← La Sala</button>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:C.logoGrad, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:9, fontWeight:600 }}>T&D</span></div>
            {!isMobile && <span style={{ fontSize:13, fontWeight:500 }}>{nombre}</span>}
            <button onClick={async()=>{await signOut();navigate('/')}} style={{ background:'none', border:`0.5px solid ${t.border}`, color:t.textSecondary, borderRadius:50, padding:'5px 14px', fontSize:12, cursor:'pointer', fontFamily:font }}>Salir</button>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth:600, margin:'0 auto', padding:isMobile?'1.5rem 1rem':'2rem 1.5rem' }}>
        <h1 style={{ fontFamily:serif, fontSize:isMobile?22:28, fontWeight:400, margin:'0 0 24px' }}><span className="td-iri-text">Mi perfil</span></h1>

        {saved && <div style={{ background:C.turquesa+'15', border:`0.5px solid ${C.turquesa}40`, borderRadius:12, padding:'10px 16px', marginBottom:16, fontSize:13, color:C.turquesa, fontWeight:500 }}>✓ Perfil actualizado</div>}

        {/* CARD PREVIEW */}
        <div style={{ background:'rgba(255,255,255,0.04)', border:`0.5px solid rgba(255,255,255,0.08)`, borderRadius:16, padding:'20px', marginBottom:28 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <span style={{ fontSize:18 }}>📇</span>
            <p style={{ margin:0, fontSize:14, fontWeight:500 }}>Tu Card de la Comunidad</p>
          </div>
          <p style={{ margin:'0 0 14px', fontSize:12, color:'rgba(255,255,255,0.5)', lineHeight:1.6 }}>
            Esta es la tarjeta que ve la gente cuando entra a Conoce Proyectos o toca tu neurona en La Sala.
          </p>
          {/* Mini preview */}
          <div style={{ display:'flex', gap:14, alignItems:'center', background:'rgba(255,255,255,0.04)', borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
            <div style={{ width:44, height:44, borderRadius:'50%', background:C.triGrad, flexShrink:0, overflow:'hidden' }}>
              {profile?.avatar_url && <img src={profile.avatar_url} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }}/>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ margin:0, fontSize:14, fontWeight:600 }}>{form.business_name || form.full_name || 'Tu proyecto'}</p>
              <p style={{ margin:'2px 0 0', fontSize:11, color:'rgba(255,255,255,0.5)' }}>
                {[form.business_type, form.business_stage, form.city].filter(Boolean).join(' · ') || 'Completa tu perfil'}
              </p>
              {form.presentacion_descripcion && <p style={{ margin:'4px 0 0', fontSize:11, color:'rgba(255,255,255,0.6)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{form.presentacion_descripcion}</p>}
            </div>
          </div>
          <a href="#form-section" style={{ display:'inline-block', fontSize:12, color:C.turquesa, fontWeight:500, textDecoration:'none' }}>Editar mi Card ↓</a>
        </div>

        <div id="form-section" />
        {/* AVATAR + LOGO */}
        <div style={{ display:'flex', gap:24, alignItems:'flex-start', marginBottom:28, flexWrap:'wrap' }}>
          {/* Photo */}
          <div style={{ textAlign:'center' }}>
            <div style={{ width:120, height:120, borderRadius:'50%', background:C.triGrad, padding:3, margin:'0 auto 10px', boxShadow:'0 0 20px rgba(93,193,185,0.2)' }}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover', display:'block' }}/>
              ) : (
                <div style={{ width:'100%', height:'100%', borderRadius:'50%', background:t.card, display:'flex', alignItems:'center', justifyContent:'center', fontSize:40, fontWeight:700, color:C.turquesa }}>{nombre.charAt(0).toUpperCase()}</div>
              )}
            </div>
            <label style={{ fontSize:12, color:C.turquesa, fontWeight:500, cursor:'pointer' }}>
              {uploading ? 'Subiendo...' : 'Cambiar foto'}
              <input type="file" accept="image/*" style={{ display:'none' }} onChange={e=>e.target.files[0] && uploadFile(e.target.files[0], 'avatars', 'avatar_url')}/>
            </label>
          </div>
          {/* Logo */}
          {membership !== 'free_observer' && membership !== 'spark_seeker' && membership !== 'doer' && (
            <div style={{ textAlign:'center' }}>
              <div style={{ width:80, height:80, borderRadius:16, border:`0.5px solid ${t.border}`, overflow:'hidden', margin:'0 auto 10px', background:t.card, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {profile?.business_logo_url ? (
                  <img src={profile.business_logo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                ) : (
                  <span style={{ fontSize:24, color:t.textSecondary }}>🏪</span>
                )}
              </div>
              <label style={{ fontSize:12, color:C.turquesa, fontWeight:500, cursor:'pointer' }}>
                {uploading ? 'Subiendo...' : 'Subir logo'}
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={e=>e.target.files[0] && uploadFile(e.target.files[0], 'logos', 'business_logo_url')}/>
              </label>
              <p style={{ fontSize:10, color:t.textSecondary, margin:'4px 0 0' }}>Aparece en tu ticket</p>
            </div>
          )}
        </div>

        {/* FORM */}
        <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:28 }}>
          <div>
            <label style={labelStyle}>Nombre completo</label>
            <input value={form.full_name} onChange={e=>updateField('full_name',e.target.value)} style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Nombre del negocio</label>
            <input value={form.business_name} onChange={e=>updateField('business_name',e.target.value)} style={inputStyle} placeholder="Ej: Postres Mía"/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Tipo de negocio</label>
              <select value={form.business_type} onChange={e=>updateField('business_type',e.target.value)} style={inputStyle}>
                <option value="">Seleccionar</option>
                <option value="comida">Comida / Bebidas</option>
                <option value="ropa">Ropa / Moda</option>
                <option value="servicios">Servicios</option>
                <option value="tecnologia">Tecnología</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Etapa</label>
              <select value={form.business_stage} onChange={e=>updateField('business_stage',e.target.value)} style={inputStyle}>
                <option value="">Seleccionar</option>
                <option value="idea">Tengo una idea</option>
                <option value="arrancando">Arrancando</option>
                <option value="estable">Estable</option>
                <option value="creciendo">Creciendo</option>
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Descripción corta ({(form.business_description||'').length}/200)</label>
            <textarea value={form.business_description} onChange={e=>e.target.value.length<=200 && updateField('business_description',e.target.value)} style={{ ...inputStyle, minHeight:80, resize:'vertical' }} placeholder="¿Qué hace tu negocio?"/>
          </div>
          <div>
            <label style={labelStyle}>Cuéntanos de ti ({(form.presentacion_descripcion||'').length}/300)</label>
            <textarea value={form.presentacion_descripcion} onChange={e=>e.target.value.length<=300 && updateField('presentacion_descripcion',e.target.value)} style={{ ...inputStyle, minHeight:100, resize:'vertical' }} placeholder="¿Qué te mueve? ¿Qué construyes? Esto aparece cuando alguien toca tu neurona."/>
          </div>
          <div>
            <label style={labelStyle}>Ciudad / Estado</label>
            <input value={form.city} onChange={e=>updateField('city',e.target.value)} style={inputStyle} placeholder="Ej: Querétaro, México"/>
          </div>
          <div>
            <label style={labelStyle}>Redes sociales</label>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:16 }}>📸</span>
                <input value={form.instagram} onChange={e=>updateField('instagram',e.target.value)} style={{ ...inputStyle, flex:1 }} placeholder="@tu_instagram"/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:16 }}>🎵</span>
                <input value={form.tiktok} onChange={e=>updateField('tiktok',e.target.value)} style={{ ...inputStyle, flex:1 }} placeholder="@tu_tiktok"/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:16 }}>👍</span>
                <input value={form.facebook} onChange={e=>updateField('facebook',e.target.value)} style={{ ...inputStyle, flex:1 }} placeholder="tu.facebook"/>
              </div>
            </div>
          </div>
        </div>

        <button className="td-glow-btn" onClick={saveProfile} disabled={saving} style={{ background:C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'13px 32px', fontSize:15, fontWeight:500, cursor:'pointer', fontFamily:font, width:isMobile?'100%':'auto', opacity:saving?0.6:1, marginBottom:28 }}>
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>

        {/* MEMBERSHIP */}
        <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'20px', marginBottom:28 }}>
          <p style={{ fontSize:12, color:t.textSecondary, margin:'0 0 4px' }}>Tu membresía actual</p>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
            <span style={{ display:'inline-block', fontSize:12, fontWeight:600, padding:'4px 14px', borderRadius:20, background:C.triGrad, color:'#fff' }}>{TIER_LABELS[membership]||'Free Observer'}</span>
            <button onClick={()=>navigate('/')} style={{ background:'none', border:`0.5px solid ${t.border}`, borderRadius:50, padding:'6px 16px', fontSize:12, color:C.turquesa, fontWeight:500, cursor:'pointer', fontFamily:font }}>Cambiar membresía</button>
          </div>
        </div>

        {/* TICKET PREVIEW */}
        {membership !== 'free_observer' && membership !== 'spark_seeker' && membership !== 'doer' && (
          <div>
            <p style={{ fontSize:13, fontWeight:500, margin:'0 0 12px' }}>Preview de tu ticket</p>
            <div style={{ background:'#fff', borderRadius:12, padding:'20px 16px', color:'#000', fontFamily:'monospace', fontSize:12, lineHeight:1.6, maxWidth:280, border:'1px solid #eee' }}>
              <div style={{ textAlign:'center', marginBottom:8 }}>
                {profile?.business_logo_url ? (
                  <img src={profile.business_logo_url} alt="" style={{ height:32, objectFit:'contain', marginBottom:4 }}/>
                ) : (
                  <p style={{ fontSize:14, fontWeight:700, margin:'0 0 2px' }}>T&D</p>
                )}
                <p style={{ fontSize:11, margin:0 }}>{form.business_name || 'Tu negocio'}</p>
              </div>
              <div style={{ borderTop:'1px dashed #ccc', margin:'6px 0' }}/>
              <p style={{ margin:0, fontSize:11 }}>Folio: TD-20260402-0001</p>
              <p style={{ margin:0, fontSize:11 }}>Producto: Ejemplo</p>
              <p style={{ margin:0, fontWeight:700 }}>$0.00</p>
              <div style={{ borderTop:'1px dashed #ccc', margin:'6px 0' }}/>
              <p style={{ textAlign:'center', fontSize:10, margin:0 }}>¡Gracias por tu compra!</p>
            </div>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <footer style={{ borderTop:`0.5px solid ${t.border}`, padding:'2rem 1.5rem', textAlign:'center' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:8 }}>
          <div style={{ width:24, height:24, borderRadius:'50%', background:C.logoGrad, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:8, fontWeight:600 }}>T&D</span></div>
          <span style={{ fontSize:13, fontWeight:500, color:t.textSecondary }}>Thinker & Doer</span>
        </div>
        <div style={{ display:'flex', justifyContent:'center', gap:16, marginTop:10 }}>
          <a href="/terminos" style={{ fontSize:11, color:'rgba(255,255,255,0.35)', textDecoration:'none' }}>Términos</a>
          <a href="/privacidad" style={{ fontSize:11, color:'rgba(255,255,255,0.35)', textDecoration:'none' }}>Privacidad</a>
        </div>
      </footer>
    </div>
  );
}
